import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Alert, AlertDescription } from '../ui/alert';
import { Upload, Trash2, FileText, Loader2 } from 'lucide-react';
import { Invoice, InvoiceStatus, SupportingDocument } from '../../types';
import {
  useSupportingDocuments,
  useUploadSupportingDocument,
  useDeleteSupportingDocument,
} from '../../hooks';
import { isFileTooLarge, MAX_UPLOAD_FILE_SIZE_MB } from '../../lib/upload';

interface SupportingDocumentsModalProps {
  invoice: Invoice;
  userRole?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SupportingDocumentsModal: React.FC<SupportingDocumentsModalProps> = ({
  invoice,
  userRole,
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation('invoices');
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [uploadError, setUploadError] = useState('');

  const { data: documents = [], isLoading, error } = useSupportingDocuments(invoice.id);
  const uploadMutation = useUploadSupportingDocument();
  const deleteMutation = useDeleteSupportingDocument();

  const canModify =
    (userRole === 'admin' || userRole === 'users.role.admin') ||
    (invoice.status !== InvoiceStatus.REJECTED &&
     invoice.status !== InvoiceStatus.APPROVED &&
     invoice.status !== InvoiceStatus.PAID);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      if (isFileTooLarge(selected)) {
        setFile(null);
        setUploadError(t('upload.error.fileTooLarge', { max: MAX_UPLOAD_FILE_SIZE_MB }));
        e.target.value = '';
        return;
      }
      setFile(selected);
      setUploadError('');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setUploadError('Please select a file');
      return;
    }

    try {
      await uploadMutation.mutateAsync({
        invoiceId: invoice.id,
        file,
        description: description.trim() || undefined,
      });
      setFile(null);
      setDescription('');
      setUploadError('');
      const fileInput = document.getElementById('file-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error: any) {
      setUploadError(error.response?.data?.message || 'Failed to upload document');
    }
  };

  const handleDelete = async (documentId: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await deleteMutation.mutateAsync({
        invoiceId: invoice.id,
        documentId,
      });
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete document');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Supporting Documents</DialogTitle>
          <DialogDescription>
            Manage supporting documents for invoice: {invoice.invoiceMonth} - {invoice.fileName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {canModify && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h3 className="text-sm font-semibold mb-3">Upload New Document</h3>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="file-upload">File</Label>
                  <Input
                    id="file-upload"
                    type="file"
                    onChange={handleFileChange}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('generator.supportingDocuments.maxFileHint', {
                      max: MAX_UPLOAD_FILE_SIZE_MB,
                    })}
                  </p>
                </div>

                <div>
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Add a description for this document..."
                    className="mt-1"
                    rows={2}
                  />
                </div>

                {uploadError && (
                  <Alert variant="destructive">
                    <AlertDescription>{uploadError}</AlertDescription>
                  </Alert>
                )}

                <Button
                  onClick={handleUpload}
                  disabled={!file || uploadMutation.isPending}
                  className="w-full"
                >
                  {uploadMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload Document
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {!canModify && (
            <Alert>
              <AlertDescription>
                Documents cannot be added or removed for invoices with status:{' '}
                {invoice.status.replace('_', ' ').toUpperCase()}
              </AlertDescription>
            </Alert>
          )}

          <div>
            <h3 className="text-sm font-semibold mb-3">
              Existing Documents ({documents.length})
            </h3>

            {error && (
              <Alert variant="destructive" className="mb-3">
                <AlertDescription>
                  Error loading documents: {(error as any)?.response?.data?.message || (error as Error)?.message || 'Unknown error'}
                </AlertDescription>
              </Alert>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No supporting documents yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {documents.map((doc: SupportingDocument) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                          <a
                            href={doc.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-primary hover:underline truncate"
                          >
                            {doc.fileName}
                          </a>
                        </div>
                      {doc.description && (
                        <p className="text-xs text-muted-foreground mt-1 ml-6">
                          {doc.description}
                        </p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 ml-6">
                        <span>{formatFileSize(doc.fileSize)}</span>
                        <span>•</span>
                        <span>{new Date(doc.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 ml-4">
                      <Button
                        variant="ghost"
                        size="icon"
                        asChild
                        title="View"
                      >
                        <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer">
                          <FileText className="h-4 w-4" />
                        </a>
                      </Button>

                      {canModify && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(doc.id)}
                          disabled={deleteMutation.isPending}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
